import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { DataSource } from '@angular/cdk/table';
import type { CollectionViewer } from '@angular/cdk/collections';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ProjectListTableComponent } from '../../../project-list/components/project-list-table/project-list-table.component';
import { ProjectDataActions } from '../../store/project-data/project-data.actions';
import { ProductTreeService } from '../../../core/services/productTree.service';
import { SnackbarActions } from '../../../core/store/snackbar/snackbar.actions';
import type { SnackbarData } from '../../../core/models/snackbar.model';
import { TranslateService } from '@ngx-translate/core';
import { ProjectEntityService } from '../../../core/services/project.service';
import { pageSizeOptions } from '../../../project-list/store/project-entity-list/project-entity-list.state';
import { PageEvent } from '@angular/material/paginator';
import { Entity } from '../../../core/models/entity.model';

const successSnackBarDuration = 10000;

@Component({
  selector: 'app-product-copy-dialog',
  templateUrl: './product-copy-dialog.component.html',
  styleUrls: ['./product-copy-dialog.component.scss'],
  standalone: false
})
export class ProductCopyDialogComponent implements OnInit, OnDestroy {
  dataSource: ProductCopyDialogDataSource;

  displayedColumns = ['name', 'projectNumber', 'country', 'select'];

  public titleTranslationParams: Record<string, string> = {};

  private projectListLoadingSubscription: Subscription;

  protected isCopying = false;

  public page = 0;
  public pageSize = pageSizeOptions[0];
  public searchString = '';
  public totalItems = 0;

  private readonly hoodName: string;

  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: { hood: Entity },
    public dialogRef: MatDialogRef<ProjectListTableComponent>,
    private store: Store,
    private productTreeService: ProductTreeService,
    private projectService: ProjectEntityService,
    private translateService: TranslateService
  ) {
    this.dataSource = new ProductCopyDialogDataSource();

    this.hoodName = this.data.hood.Properties['Name'].Value as string;

    this.titleTranslationParams = { '0': this.hoodName };
  }

  ngOnInit(): void {
    this.refreshData();
  }

  ngOnDestroy(): void {
    this.projectListLoadingSubscription?.unsubscribe();
  }

  onFilterInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    // this.dataSource.filterData(target.value);
    this.searchString = target.value;
    this.refreshData();
  }

  onCopy(selectedProject: ProjectListItem): void {
    this.isCopying = true;
    this.productTreeService.copy(this.data.hood.Id, selectedProject.id).subscribe({
      next: copiedId => this.onSuccess(selectedProject, copiedId),
      error: error => of(ProjectDataActions.copy_entity_fail({ error })),
      complete: () => (this.isCopying = false)
    });
  }

  handlePageEvent($event: PageEvent) {
    this.pageSize = $event.pageSize;
    this.page = $event.pageIndex;
    this.refreshData();
  }

  private refreshData(): void {
    if (this.projectListLoadingSubscription) {
      this.projectListLoadingSubscription.unsubscribe();
    }
    this.projectListLoadingSubscription = this.projectService
      .queryProjectEntities(this.searchString, this.page, this.pageSize)
      .subscribe(list => {
        this.dataSource.updateData(list.Items);
        this.totalItems = list.TotalItems;
      });
  }

  private onSuccess(selectedProject: ProjectListItem, copiedId: string): void {
    const snackbarData: SnackbarData = {
      type: 'info',
      message: this.translateService.instant('UI.ProductCopy.Copied', { '0': this.hoodName, '1': selectedProject.name }),
      closeButtonText: this.translateService.instant('UI.ProductCopy.GoToProject'),
      closeButtonLink: `/projects/${selectedProject.id}`
    };
    this.store.dispatch(ProjectDataActions.copy_entity_success({ copiedId, parentId: selectedProject.id }));
    this.store.dispatch(SnackbarActions.snackbar_open({ snackbarData, config: { duration: successSnackBarDuration } }));
    this.dialogRef.close(true);
  }

  protected readonly pageSizeOptions = pageSizeOptions;
}

interface ProjectListItem {
  id: string;
  name: string;
  projectNumber: string;
  country: string;
}

class ProductCopyDialogDataSource extends DataSource<ProjectListItem> {
  private subject = new BehaviorSubject<ProjectListItem[]>([]);

  connect(_: CollectionViewer): Observable<ProjectListItem[]> {
    return this.subject.asObservable();
  }

  disconnect(_: CollectionViewer): void {
    this.subject.complete();
  }

  updateData(entities: Entity[]): void {
    const items = entities.map<ProjectListItem>(project => {
      const countryCode = project.Properties['CountryCode'].Value;
      const countryValue = `Country.${countryCode}`;

      return {
        id: project.Id,
        country: countryValue,
        name: project.Properties['Name'].Value as string,
        projectNumber: project.Properties['ProjectNumber'].Value as string
      };
    });

    this.subject.next(items);
  }
}
